import {
  CANDIDATE_STATUS_LABELS_INTERNAL,
  COUNTRY_CODES,
  calendarDaysAgo,
  fmtDate,
  relativeDate,
  type CandidateStatus,
} from '@remote-medium/shared';
import {
  Badge,
  Button,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  SearchableSelect,
  unwrap,
  useApi,
} from '@remote-medium/ui';
import { ChevronLeft, ChevronRight, FileText, Search } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';

import { API_ORIGIN, admin } from '../../lib/client.ts';
import { CandidateStatusChip } from '../../lib/status-chip.tsx';
import {
  Chips,
  DataTable,
  EmptyRow,
  LinkButton,
  None,
  Page,
  PageHeader,
  ROW_LINK,
  StatusRow,
  Tabs,
  tableReady,
} from '../../lib/ui.tsx';

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
const COUNTRY_OPTIONS = [...COUNTRY_CODES]
  .map((code) => ({ value: code, label: regionNames.of(code) ?? code }))
  .sort((a, b) => a.label.localeCompare(b.label));

// Status tabs always shown; screened/placed appear only while non-empty.
const TAB_STATUSES: CandidateStatus[] = ['new', 'active', 'paused', 'withdrawn'];
const EXTRA_STATUSES: CandidateStatus[] = ['screened', 'placed'];

const MAX_SKILLS = 3;

// No test-account flag exists on candidate; e2e and seed accounts use the
// reserved .local domain (docs/TODO.md "Curator candidates").
const isTestAccount = (email: string) => email.toLowerCase().endsWith('.local');

const joined = (iso: string) => {
  if (calendarDaysAgo(iso) >= 7) return fmtDate(iso);
  const r = relativeDate(iso);
  return r.charAt(0).toUpperCase() + r.slice(1);
};

export function CandidatesPage() {
  const [params, setParams] = useSearchParams();
  const query = params.toString();
  const { data, error, loading } = useApi(
    () => unwrap(admin.candidates.$get({ query: Object.fromEntries(params) })),
    [query],
  );
  const { data: taxonomy } = useApi(() => unwrap(admin.taxonomy.$get()), []);

  const roleTypeOptions = useMemo(
    () =>
      (taxonomy?.roleTypes ?? [])
        .map((rt) => ({ value: rt.id, label: rt.name, group: rt.family }))
        .sort((a, b) => a.group.localeCompare(b.group) || a.label.localeCompare(b.label)),
    [taxonomy],
  );
  const skillOptions = useMemo(
    () => (taxonomy?.skills ?? []).map((s) => ({ value: s.id, label: s.name })),
    [taxonomy],
  );

  // The API already orders new candidates oldest first, then the rest newest first.
  const rows = data?.candidates ?? [];

  const ready = tableReady({ data, loading, error });

  // Any filter change starts again from the first page.
  const set = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: true });
  };

  // A page past the end (an old link, or fewer matches since) jumps to the
  // last page that has rows instead of showing an empty table.
  const lastPage = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  useEffect(() => {
    if (data && data.page > lastPage) set('page', lastPage === 1 ? null : String(lastPage));
  }, [data, lastPage]);

  // Counts follow the search, role type, skill and country filters; each tab
  // shows what it would list.
  const byStatus = data?.byStatus ?? {};
  const allCount = Object.values(byStatus).reduce<number>((n, v) => n + (v ?? 0), 0);
  const status = params.get('status');
  const tabs = [
    { value: 'all', label: 'All', count: data ? allCount : undefined },
    ...[...TAB_STATUSES, ...EXTRA_STATUSES.filter((s) => byStatus[s] || status === s)].map((s) => ({
      value: s,
      label: CANDIDATE_STATUS_LABELS_INTERNAL[s],
      count: data ? (byStatus[s] ?? 0) : undefined,
    })),
  ];
  const filtered = [...params.keys()].some((k) => k !== 'status' && k !== 'page');

  return (
    <Page className="gap-6">
      <PageHeader
        title="Candidates"
        description="New candidates aren't ranked against any role until you screen them. Oldest first."
      />

      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <Tabs
          className="xl:shrink-0 xl:flex-nowrap"
          label="Filter by status"
          tabs={tabs}
          value={status ?? 'all'}
          onChange={(v) => set('status', v === 'all' ? null : v)}
        />

        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:flex xl:min-w-0 xl:flex-1 xl:items-center xl:justify-end">
          <InputGroup className="xl:w-auto xl:max-w-60 xl:min-w-32 xl:flex-1">
            <InputGroupAddon>
              <Search aria-hidden="true" />
            </InputGroupAddon>
            <InputGroupInput
              key={params.get('q') ?? ''}
              type="search"
              placeholder="Name or email"
              aria-label="Search by name or email"
              defaultValue={params.get('q') ?? ''}
              onKeyDown={(e) => {
                if (e.key === 'Enter') set('q', e.currentTarget.value.trim());
              }}
            />
          </InputGroup>
          <div className="xl:max-w-44 xl:min-w-28 xl:flex-1">
            <SearchableSelect
              aria-label="Role type"
              options={roleTypeOptions}
              value={params.get('roleTypeId')}
              onChange={(v) => set('roleTypeId', v)}
              clearable
              placeholder="Any role type"
            />
          </div>
          <div className="xl:max-w-44 xl:min-w-28 xl:flex-1">
            <SearchableSelect
              aria-label="Skill"
              options={skillOptions}
              value={params.get('skillId')}
              onChange={(v) => set('skillId', v)}
              clearable
              placeholder="Any skill"
            />
          </div>
          <div className="xl:max-w-44 xl:min-w-28 xl:flex-1">
            <SearchableSelect
              aria-label="Country"
              options={COUNTRY_OPTIONS}
              value={params.get('country')}
              onChange={(v) => set('country', v)}
              clearable
              placeholder="Any country"
            />
          </div>
          {filtered && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const next = new URLSearchParams();
                if (status) next.set('status', status);
                setParams(next, { replace: true });
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <DataTable minWidth="min-w-4xl">
          <thead>
            <tr>
              <th>Candidate</th>
              <th>Status</th>
              <th>Targets</th>
              <th>Skills</th>
              <th>Country</th>
              <th>CV</th>
              <th>Joined</th>
              <th>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <StatusRow colSpan={8} loading={loading} error={error} />
            {ready && rows.length === 0 && (
              <EmptyRow colSpan={8}>No candidates match these filters.</EmptyRow>
            )}
            {ready &&
              rows.map((c) => (
                <tr key={c.id} className="hover:bg-muted/50">
                  <td className="max-w-72">
                    <div className="flex items-center gap-2">
                      <Link to={`/candidates/${c.id}`} className={ROW_LINK}>
                        {c.fullName}
                      </Link>
                      {isTestAccount(c.email) && (
                        <Badge variant="external" className="font-normal">
                          Test account
                        </Badge>
                      )}
                    </div>
                    <div className="truncate text-muted-foreground" title={c.email}>
                      {c.email}
                    </div>
                  </td>
                  <td>
                    <CandidateStatusChip status={c.status} />
                  </td>
                  <td>
                    {c.roleTargets.length ? (
                      c.roleTargets.map((t) => <div key={t}>{t}</div>)
                    ) : (
                      <None />
                    )}
                  </td>
                  <td>
                    <Chips items={c.skills} max={MAX_SKILLS} />
                  </td>
                  <td className="whitespace-nowrap">{regionNames.of(c.country) ?? c.country}</td>
                  <td>
                    {c.hasCv ? (
                      <a
                        href={`${API_ORIGIN}/admin/candidates/${c.id}/cv`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-foreground no-underline hover:underline"
                      >
                        <FileText aria-hidden="true" className="size-4" />
                        CV
                      </a>
                    ) : (
                      <None />
                    )}
                  </td>
                  <td className="whitespace-nowrap text-muted-foreground tabular-nums">
                    {joined(c.createdAt)}
                  </td>
                  <td className="text-right">
                    {c.status === 'new' && (
                      <LinkButton to={`/candidates/${c.id}`}>Screen</LinkButton>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </DataTable>
        {ready && data && (
          <Pager
            page={data.page}
            pageSize={data.pageSize}
            shown={rows.length}
            total={data.total}
            onPage={(p) => set('page', p === 1 ? null : String(p))}
          />
        )}
      </div>
    </Page>
  );
}

function Pager({
  page,
  pageSize,
  shown,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  shown: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const first = (page - 1) * pageSize + 1;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="m-0 text-xs text-muted-foreground tabular-nums">
        {shown
          ? `Showing ${first}–${first + shown - 1} of ${total} candidates`
          : `Showing 0 of ${total} candidates`}
      </p>
      {pages > 1 && (
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
            <ChevronLeft data-icon="inline-start" />
            Previous
          </Button>
          <span className="text-xs text-muted-foreground tabular-nums">
            Page {page} of {pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= pages}
            onClick={() => onPage(page + 1)}
          >
            Next
            <ChevronRight data-icon="inline-end" />
          </Button>
        </div>
      )}
    </div>
  );
}
